import { useState } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Icon } from '../../../../../components/common/icon/Icon';
import { IProductFilter, IProductFilterOption } from '../../../../../constants/product/productFilter.constant';
import { useGetFilterHasSelectedOption } from '../../../../../hooks/filter/useGetFilterHasSelectedOption';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store/useStore';
import { productFilterSlice } from '../../../../../store/productFilterSlice';
import { ProductFilterSlideModal } from '../ProductFilterSlideModal/ProductFilterSlideModal';
import { ProductFilterButton } from './components/ProductFilterButton';
import { ProductFilterOption } from './components/ProductFilterOption';

export const ProductFilterSection = () => {
  const { isFilterHasSelectedOption } = useGetFilterHasSelectedOption({ filter: 'all' });

  const dispatch = useAppDispatch();
  const productFilterStates = useAppSelector((state) => state.productFilter);
  const productFilterActions = productFilterSlice.actions;

  const [isShowFilterSlideModal, setIsShowFilterSlideModal] = useState<boolean>(false);

  const closeFilterSlideModal = () => setIsShowFilterSlideModal(false);

  const handleResetGlobalFiltersButtonClick = () => {
    dispatch(productFilterActions.resetAllFilters());
  };

  const handleFilterButtonClick = (filter: IProductFilter, filterIndex: number) => {
    ampli.pvmClickPlpProductFilterButton({ filter_key: filter.key });
    ReactGA.event('pvm_click_plp_product_filter_button', { filter_key: filter.key });

    setIsShowFilterSlideModal(true);
    dispatch(productFilterActions.changeHaveToRefreshLocalStates(true));
    dispatch(productFilterActions.changeClickedFilterButtonIndex(filterIndex));
  };

  const handleSelectedFilterOptionClick = (filter: IProductFilter, option: IProductFilterOption) => {
    if (filter.optionType === 'radio') {
      dispatch(productFilterActions.resetOneFilter(filter));
    } else {
      dispatch(
        productFilterActions.changeProductFilterOptionIsSelected({
          targetFilter: filter,
          targetOption: option,
        }),
      );
    }
  };

  return (
    <Container>
      <ProductFilterTopSection>
        {isFilterHasSelectedOption && (
          <ResetGlobalFiltersSection>
            <ResetGlobalFiltersButton onClick={handleResetGlobalFiltersButtonClick}>
              <Icon name={'Reset'} width={20} height={20} />
            </ResetGlobalFiltersButton>
          </ResetGlobalFiltersSection>
        )}

        <BlurSectionLeft top={16} left={isFilterHasSelectedOption ? 46 : 0} />
        <ProductFilterButtonContainer>
          {productFilterStates.globalProductFilters.map((filter, filterIndex) => (
            <ProductFilterButton filter={filter} onClick={() => handleFilterButtonClick(filter, filterIndex)} />
          ))}
        </ProductFilterButtonContainer>
        <BlurSectionRight top={16} right={0} />
      </ProductFilterTopSection>

      {isFilterHasSelectedOption && (
        <ProductFilterBottomSection>
          <BlurSectionLeft top={0} left={0} />
          <ProductFilterOptionContainer>
            {productFilterStates.globalProductFilters.map((filter) => {
              return (
                <>
                  {filter.options.map((option) => {
                    return (
                      option.isSelected &&
                      !option.isDefault && (
                        <ProductFilterOption
                          key={option.value}
                          option={option}
                          onClick={() => handleSelectedFilterOptionClick(filter, option)}
                        />
                      )
                    );
                  })}
                </>
              );
            })}
          </ProductFilterOptionContainer>
          <BlurSectionRight top={0} right={0} />
        </ProductFilterBottomSection>
      )}

      <ProductFilterSlideModal isShow={isShowFilterSlideModal} closeModal={closeFilterSlideModal} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ProductFilterTopSection = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  position: relative;

  padding: var(--radius-large, 16px) 0px;
`;

const ResetGlobalFiltersSection = styled.div`
  padding-left: 16px;
`;

const ResetGlobalFiltersButton = styled.div`
  min-width: 30px;
  min-height: 30px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-offblack, #505363);

  cursor: pointer;
`;

const ProductFilterBottomSection = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  position: relative;

  padding-bottom: 16px;
`;

const ProductFilterButtonSection = styled.div`
  width: 100%;

  position: relative;
`;

const ProductFilterButtonContainer = styled.div`
  display: flex;
  align-items: center;

  padding: 0px var(--radius-large, 16px);
  gap: var(--radius-medium, 8px);

  overflow-x: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const BlurSection = styled.div`
  display: flex;
  width: var(--radius-large, 16px);
  height: 30px;
  justify-content: center;
  align-items: center;

  position: absolute;

  border-radius: var(--radius-none, 0px);
`;

const BlurSectionLeft = styled(BlurSection)<{ top: number; left: number }>`
  top: ${(props) => `${props.top}px`};
  left: ${(props) => `${props.left}px`};
  background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, #fff 50.49%);
`;

const BlurSectionRight = styled(BlurSection)<{ top: number; right: number }>`
  top: ${(props) => `${props.top}px`};
  right: ${(props) => `${props.right}px`};
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 49.6%);
`;

const ProductFilterOptionContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 16px;

  gap: 5px;

  overflow-x: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;
